import React from 'react';
import PropTypes from 'prop-types';

/*
 * Localized without importing i18next on purpose. The boundary is mounted above the provider in
 * `index.js` and must not depend on anything that can itself throw, so the three strings live
 * here and the language is read straight off the same localStorage key `i18n.js` uses.
 */
const FALLBACK_TEXT = {
  fi: {
    title: 'Sivun lataus epäonnistui',
    body: 'Päivitä sivu. Jos ongelma toistuu, ota yhteyttä ylläpitoon.',
    reload: 'Päivitä sivu',
  },
  en: {
    title: 'The page failed to load',
    body: 'Reload the page. If the problem persists, contact support.',
    reload: 'Reload page',
  },
  sv: {
    title: 'Sidan kunde inte laddas',
    body: 'Ladda om sidan. Kontakta supporten om problemet kvarstår.',
    reload: 'Ladda om sidan',
  },
};

function getFallbackText() {
  try {
    return FALLBACK_TEXT[localStorage.getItem('selectedLanguage')] || FALLBACK_TEXT.fi;
  } catch (e) {
    return FALLBACK_TEXT.fi;
  }
}

/*
 * A last resort between a render failure and a blank browser window.
 *
 * WHY IT SITS ABOVE THE APPLICATION. React unmounts the entire tree when a render throws and
 * nothing catches it, so the person sees a white page and reports "the site is broken" — with
 * no further detail, because there is nothing on screen to describe. React's own console output
 * recommends exactly this component when it happens (OVIO-7).
 *
 * A boundary catches errors BELOW itself, never its own, so this is mounted in `index.js`
 * around the provider rather than inside `App`: the failure it was written for was in
 * `App.render` itself.
 *
 * Deliberately not a friendly retry screen. It says what happened, offers a reload, and gets
 * out of the way. A boundary that tries to be clever is another thing that can throw.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error, info) {
    // Left on the console on purpose: this project has no browser error reporter wired, and a
    // swallowed stack is how a white page becomes unexplainable a second time.
    // eslint-disable-next-line no-console
    console.error('Unhandled render error', error, info);
  }

  render() {
    const { failed } = this.state;
    const { children } = this.props;
    if (!failed) {
      return children;
    }
    const text = getFallbackText();
    return (
      <div style={{ padding: '2rem', fontFamily: 'sans-serif' }}>
        <h1 style={{ fontSize: '1.25rem' }}>{text.title}</h1>
        <p>{text.body}</p>
        <button type="button" onClick={() => window.location.reload()}>
          {text.reload}
        </button>
      </div>
    );
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node,
};

ErrorBoundary.defaultProps = {
  children: null,
};
